/**
 * OsDetailDialog — modal completo de Ordem de Serviço (UX-1 / FEAT-V2-007 + TD-V2-UI-01).
 *
 * Estrutura: tabs análogos ao DealDetailDialog do Comercial, mais state machine
 * inline + modal de links públicos.
 *
 * Tabs (FASE-C: emoji-ícone banido — rótulos limpos):
 *   - Detalhes: read-only com cliente, endereço, prioridade, status,
 *               agendamento, técnico
 *   - Atividade: timeline unificada (atividades + tasks)
 *   - Notas internas: privacy guarantee (Bloco C)
 *   - Relacionados: card_relations (Bloco D)
 *
 * FASE-C (matar enfeite): a aba "Comentários" (placeholder sem função) e o
 * botão "Baixar PDF" (stub 501) foram REMOVIDOS da UI — voltam quando a
 * função existir de verdade.
 *
 * State machine: aberta → atribuída → em_campo → concluída (+ cancelada).
 */

import { useMemo, useState } from 'react';
// FASE-C (design system): Lucide only.
import {
  Archive,
  ArchiveRestore,
  CalendarDays,
  Copy,
  Link2,
  MapPin,
  RefreshCw,
  Trash2,
} from 'lucide-react';

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useConfirmDialog,
} from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '@/lib/admin-store';
import { useClientNome } from '@/lib/clients-store';
import {
  useOsExternalLinks,
  useServiceOrderActions,
  type OsExternalLink,
  type OsStatus,
  type ServiceOrder,
} from '@/lib/service-orders-store';
// K-02: campos personalizados da OS — entidade 'manutencao' no EAV.
import { CamposPersonalizadosBloco } from '@/components/dynamic-form';
import { InternalNotesTab } from '@/components/cards/InternalNotesTab';
import { RelatedCardsTab } from '@/components/cards/RelatedCardsTab';
import { CardIdBadge } from '@/components/cards/CardIdBadge';
// UI-1 / TD-V2-PERM-01: colaboradores na OS.
import { CardCollaboratorsSection } from '@/components/cards/CardCollaboratorsSection';
import { formatDateTime } from '@/lib/format-date';
// BUG-V2-047: hook canônico pra arquivar/reativar OS.
import { useInativarCard } from '@/lib/cards-soft-delete';
import { useToast } from '@/components/ui';

// ─── State machine ──────────────────────────────────────────────────────

const STATUS_LABELS: Record<OsStatus, { label: string; variant: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }> = {
  aberta: { label: 'Aberta', variant: 'neutral' },
  atribuida: { label: 'Atribuída', variant: 'info' },
  em_campo: { label: 'Em Campo', variant: 'warning' },
  concluida: { label: 'Concluída', variant: 'success' },
  cancelada: { label: 'Cancelada', variant: 'danger' },
};

/**
 * Próximas transições válidas por status. `cancelada` é alvo possível de
 * QUALQUER estado não-final.
 */
function nextStatuses(current: OsStatus): OsStatus[] {
  switch (current) {
    case 'aberta':
      return ['atribuida', 'cancelada'];
    case 'atribuida':
      return ['em_campo', 'cancelada'];
    case 'em_campo':
      return ['concluida', 'cancelada'];
    case 'concluida':
    case 'cancelada':
      return []; // estados finais
  }
}

const PRIORIDADE_LABELS: Record<string, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

// ─── Props ──────────────────────────────────────────────────────────────

interface OsDetailDialogProps {
  os: ServiceOrder;
  onClose: () => void;
}

export function OsDetailDialog({ os, onClose }: OsDetailDialogProps) {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const clienteNome = useClientNome(os.clienteId);
  const allUsers = useUsers();
  const tecnico = useMemo(
    () => (os.tecnicoId ? allUsers.find((u) => u.id === os.tecnicoId) ?? null : null),
    [allUsers, os.tecnicoId],
  );
  const { changeStatus, pending } = useServiceOrderActions();

  const [activeTab, setActiveTab] = useState<string>('detalhes');
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const statusMeta = STATUS_LABELS[os.status];
  const canChangeStatus =
    isAdmin || (user?.id && user.id === os.tecnicoId) || os.criadoPorId === user?.id;

  // BUG-V2-047: soft delete via hook canônico.
  const inativarCard = useInativarCard();
  const confirm = useConfirmDialog();
  const { show: showToast } = useToast();
  const canInativar = user?.podeInativarCards ?? false;
  const isInativo = os.ativo === false;

  async function handleToggleAtivo() {
    const proximoAtivo = isInativo;
    const verbo = proximoAtivo ? 'Reativar' : 'Arquivar';
    const ok = await confirm({
      title: `${verbo} ${os.humanLabel ?? 'OS'}?`,
      message: proximoAtivo
        ? 'A OS volta a aparecer no Kanban e nas listagens normais.'
        : 'A OS some do Kanban e das listagens. Quem tiver "Mostrar inativos" ativo ainda vê (com badge cinza). Você pode reativar a qualquer momento.',
      confirmLabel: verbo,
      cancelLabel: 'Cancelar',
      variant: proximoAtivo ? 'default' : 'destructive',
    });
    if (!ok) return;
    inativarCard.mutate(
      { type: 'service_order', id: os.id, ativo: proximoAtivo },
      {
        onSuccess: () => {
          showToast(proximoAtivo ? 'OS reativada' : 'OS arquivada');
          if (!proximoAtivo) onClose();
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Erro ao alterar status';
          showToast(`Falha: ${msg}`);
        },
      },
    );
  }

  async function handleChangeStatus(novoStatus: OsStatus) {
    setStatusError(null);
    try {
      await changeStatus(os.id, novoStatus);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Erro ao mudar status');
    }
  }

  // FASE-C (matar enfeite): o botão "Baixar PDF" era stub (backend 501) e foi
  // ESCONDIDO da UI — regra do handoff: se não funciona, ou termina ou some.
  // Quando o PDF real existir, reintroduzir o botão aqui com o download.

  const clienteDisplay = clienteNome || (os.clienteId.startsWith('client-') ? '…' : os.clienteId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="lg" data-testid="os-detail-dialog">
      <DialogHeader>
        <DialogTitle>
          <span className="block text-2xl">{os.titulo}</span>
          <span className="block text-sm text-ink-2 dark:text-ink-dark-2 font-normal mt-1">
            {clienteDisplay} · {os.tipo}
          </span>
          {/* BUG-V2-045: exibe #N humano em vez de UUID.
              BUG-V2-047: badge cinza pra OSs arquivadas. */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <CardIdBadge cardId={os.id} humanLabel={os.humanLabel} variant="block" />
            {isInativo && (
              <Badge variant="neutral" size="sm" data-testid="card-inativo-badge">
                Inativo
              </Badge>
            )}
          </div>
        </DialogTitle>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Badge variant={statusMeta.variant} dot data-testid="os-status-badge">
            {statusMeta.label}
          </Badge>
          <Badge variant="neutral" size="sm">
            {PRIORIDADE_LABELS[os.prioridade] ?? os.prioridade}
          </Badge>
          {os.agendadoParaIso && (
            <Badge variant="info" size="sm" title="Agendamento">
              <CalendarDays className="w-3 h-3 mr-0.5" aria-hidden="true" />
              {formatDateTime(os.agendadoParaIso)}
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setShowLinksModal(true)}
              data-testid="os-gerar-link-btn"
            >
              <Link2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Gerar link público
            </Button>
            {/* FASE-C: "Baixar PDF" (stub 501) escondido — ver comentário acima. */}
          </div>
        </div>
      </DialogHeader>

      <DialogContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* FASE-C: rótulos sem emoji; aba "Comentários" (placeholder) removida. */}
          <TabsList className="mb-4">
            <TabsTrigger value="detalhes" data-testid="os-tab-detalhes">Detalhes</TabsTrigger>
            <TabsTrigger value="atividade" data-testid="os-tab-atividade">Atividade</TabsTrigger>
            <TabsTrigger value="notas-internas" data-testid="os-tab-notas-internas">Notas internas</TabsTrigger>
            <TabsTrigger value="relacionados" data-testid="os-tab-relacionados">Relacionados</TabsTrigger>
          </TabsList>

          {/* ─── Detalhes ────────────────────────────────────────────── */}
          <TabsContent value="detalhes">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Section title="Cliente">
                  <p className="text-sm">{clienteDisplay}</p>
                </Section>
                <Section title="Endereço">
                  <p className="text-sm whitespace-pre-wrap">{os.endereco}</p>
                  {os.latitude != null && os.longitude != null && (
                    <a
                      href={`https://www.google.com/maps?q=${os.latitude},${os.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-red hover:underline"
                    >
                      <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      Ver no Google Maps
                    </a>
                  )}
                </Section>
                <Section title="Técnico">
                  {tecnico ? (
                    <div className="flex items-center gap-2">
                      <Avatar name={tecnico.nome} src={tecnico.fotoUrl} size="sm" />
                      <span className="text-sm">{tecnico.nome}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 italic">
                      Nenhum técnico atribuído
                    </p>
                  )}
                </Section>
                {/* UI-1 / TD-V2-PERM-01: seção Colaboradores no modal OS. */}
                <CardCollaboratorsSection cardId={os.id} deptSlug="manutencao" />
                <Section title="Datas">
                  <p className="text-xs">Criado: {formatDateTime(os.criadoEmIso)}</p>
                  <p className="text-xs">Atualizado: {formatDateTime(os.atualizadoEmIso)}</p>
                  {os.inicioAtendimentoIso && (
                    <p className="text-xs">Início campo: {formatDateTime(os.inicioAtendimentoIso)}</p>
                  )}
                  {os.fimAtendimentoIso && (
                    <p className="text-xs">Fim campo: {formatDateTime(os.fimAtendimentoIso)}</p>
                  )}
                </Section>
              </div>

              <div className="lg:col-span-3 space-y-4">
                {os.descricao && (
                  <Section title="Descrição">
                    <p className="text-sm whitespace-pre-wrap">{os.descricao}</p>
                  </Section>
                )}
                {os.observacoes && (
                  <Section title="Observações">
                    <p className="text-sm whitespace-pre-wrap">{os.observacoes}</p>
                  </Section>
                )}

                {/*
                  K-02 — campos personalizados da OS, na coluna larga da aba
                  Detalhes, logo abaixo do que descreve o serviço (descrição e
                  observações) e ANTES da mudança de status. A ordem é
                  deliberada: quem está com a OS aberta preenche o que sabe e
                  só então move o estado; se o bloco viesse depois dos botões
                  de transição, o técnico mudaria o status e fecharia o modal
                  sem nunca ver os campos.

                  A entidade no EAV é 'manutencao' — a mesma que o construtor
                  oferece como "OS / Manutenção".
                */}
                <CamposPersonalizadosBloco
                  entidade="manutencao"
                  entidadeId={os.id}
                  testId="os-campos-personalizados"
                  ajuda="Campos extras que o administrador criou pras ordens de serviço, em Configurações › Catálogos › Campos personalizados. Valem pra todas as OS."
                />

                {/* ─── State machine inline ─────────────────────────── */}
                <Section title="Mudar status">
                  {!canChangeStatus ? (
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 italic">
                      Você não tem permissão pra alterar o status desta OS.
                    </p>
                  ) : nextStatuses(os.status).length === 0 ? (
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 italic">
                      Status final: nenhuma transição possível.
                    </p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2" data-testid="os-status-transitions">
                        {nextStatuses(os.status).map((s) => {
                          const meta = STATUS_LABELS[s];
                          const variant: 'destructive' | 'secondary' =
                            s === 'cancelada' ? 'destructive' : 'secondary';
                          return (
                            <Button
                              key={s}
                              type="button"
                              size="sm"
                              variant={variant}
                              loading={pending === os.id}
                              onClick={() => handleChangeStatus(s)}
                              data-testid={`os-status-btn-${s}`}
                            >
                              → {meta.label}
                            </Button>
                          );
                        })}
                      </div>
                      {statusError && (
                        <Alert variant="danger" rail className="mt-2 text-xs">
                          {statusError}
                        </Alert>
                      )}
                    </>
                  )}
                </Section>
              </div>
            </div>
          </TabsContent>

          {/* ─── Atividade ───────────────────────────────────────────── */}
          {/* Timeline unificada (componente compartilhado, activity + task). */}
          <TabsContent value="atividade">
            <UnifiedTimelineTab cardId={os.id} workflowSlug="manutencao" cardType="service_order" />
          </TabsContent>

          {/* FASE-C: aba "Comentários" removida — era placeholder informativo
              sem função (o backend de comentários de OS ainda não existe).
              Comunicação interna segue em "Notas internas"; com o cliente, o
              link público. Quando os comentários existirem, a aba volta. */}

          {/* ─── Notas Internas (Bloco C) ────────────────────────────── */}
          <TabsContent value="notas-internas">
            <InternalNotesTab cardId={os.id} workflowSlug="manutencao" />
          </TabsContent>

          {/* ─── Relacionados (Bloco D.3) ────────────────────────────── */}
          <TabsContent value="relacionados">
            <RelatedCardsTab cardType="service_order" cardId={os.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
        {/* BUG-V2-047: Arquivar/Reativar OS — gate de permissão. */}
        {canInativar && (
          <Button
            variant={isInativo ? 'secondary' : 'ghost'}
            onClick={handleToggleAtivo}
            disabled={inativarCard.isPending}
            data-testid="card-toggle-ativo-btn"
          >
            {isInativo ? (
              <>
                <ArchiveRestore className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Reativar
              </>
            ) : (
              <>
                <Archive className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Arquivar
              </>
            )}
          </Button>
        )}
      </DialogFooter>

      {/* Modal aninhado: gerar/revogar links públicos */}
      {showLinksModal && (
        <ExternalLinksDialog
          osId={os.id}
          onClose={() => setShowLinksModal(false)}
        />
      )}
    </Dialog>
  );
}

// ─── Subcomponente: Section ───────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-1.5">{title}</p>
      {children}
    </div>
  );
}

// FASE-C: OsActivityPlaceholder/OsCommentsPlaceholder removidos — a timeline
// real (UnifiedTimelineTab) renderiza direto; comentários voltam com backend.

// Import circular evitado: UnifiedTimelineTab fica em arquivo próprio (UX-2).
import { UnifiedTimelineTab } from '@/components/cards/UnifiedTimelineTab';

// ─── External Links Dialog ────────────────────────────────────────────

interface ExternalLinksDialogProps {
  osId: string;
  onClose: () => void;
}

function ExternalLinksDialog({ osId, onClose }: ExternalLinksDialogProps) {
  // FASE 2 / M1: ConfirmDialog
  const confirm = useConfirmDialog();
  // BUG-V2-038 (2026-05-24): hook agora usa staleTime=0 + refetchOnMount='always'
  // (ver service-orders-store.ts) pra evitar cache stale exibindo lista vazia.
  // isFetching expõe o background refresh pra UI mostrar feedback.
  const { data: links = [], isLoading, isFetching, refetch } = useOsExternalLinks(osId);
  const { createExternalLink, deleteExternalLink, pending } = useServiceOrderActions();
  const [newLinkUrl, setNewLinkUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // M-7 (BUG-V2-030): backend retorna path-relativo (`/os/<token>`) por
  // design (multi-domain). Frontend prepende window.location.origin pra
  // copy/share gerar URL absoluta clicável fora do app (WhatsApp, email,
  // SMS). Fallback pra '' em SSR/jsdom sem origin (UI ainda mostra o path).
  function toAbsoluteUrl(relativeOrAbsolute: string): string {
    if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';
    return `${origin}${relativeOrAbsolute.startsWith('/') ? '' : '/'}${relativeOrAbsolute}`;
  }

  async function handleCreate() {
    setError(null);
    setNewLinkUrl(null);
    try {
      const r = await createExternalLink(osId);
      setNewLinkUrl(toAbsoluteUrl(r.publicUrl));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar link');
    }
  }

  async function handleRevoke(link: OsExternalLink) {
    const ok = await confirm({
      title: 'Revogar link público',
      message: 'Revogar este link público? O cliente perderá acesso imediato.',
      confirmLabel: 'Revogar',
      variant: 'destructive',
    });
    if (!ok) return;
    setError(null);
    try {
      await deleteExternalLink(osId, link.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao revogar link');
    }
  }

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback silencioso
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md" data-testid="external-links-dialog">
      <DialogHeader>
        <DialogTitle>Links públicos da OS</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-3">
          Links permitem que o cliente final visualize esta OS sem fazer login.
          Conteúdo de notas internas e anexos privados NUNCA aparecem.
        </p>

        {error && (
          <Alert variant="danger" rail className="mb-3">
            {error}
          </Alert>
        )}

        {newLinkUrl && (
          <Alert variant="success" rail className="mb-3">
            <p className="text-xs font-medium mb-1">Link gerado:</p>
            <code className="text-xs break-all" data-testid="new-link-url">
              {newLinkUrl}
            </code>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => handleCopy(newLinkUrl)}
              className="ml-2"
            >
              <Copy className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Copiar
            </Button>
          </Alert>
        )}

        <Button
          type="button"
          onClick={handleCreate}
          loading={pending === `link-${osId}`}
          data-testid="create-link-btn"
          className="mb-4"
        >
          + Gerar novo link
        </Button>

        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider">
            Links existentes ({links.length})
            {isFetching && !isLoading && (
              <span
                className="ml-2 text-xs italic text-ink-3 normal-case"
                data-testid="links-refreshing"
              >
                atualizando…
              </span>
            )}
          </h4>
          {/* BUG-V2-038 (2026-05-24): botão manual de refresh — defesa
              em profundidade. Se hook por algum motivo não refizer fetch
              automaticamente, user tem ação explícita. */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void refetch()}
            disabled={isFetching}
            data-testid="links-refresh"
            title="Recarregar lista"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <p className="text-xs italic text-ink-3">Carregando…</p>
        ) : links.length === 0 ? (
          <p className="text-xs italic text-ink-3 dark:text-ink-dark-3">
            Nenhum link gerado ainda.
          </p>
        ) : (
          <ul data-testid="links-list" className="space-y-2">
            {links.map((l) => (
              <li
                key={l.token}
                data-testid={`link-${l.token}`}
                className="flex items-start justify-between gap-3 p-2 rounded border border-line dark:border-line-dark"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono truncate">
                      {l.token.slice(0, 16)}…
                    </code>
                    {l.ativo ? (
                      <Badge variant="success" size="sm">Ativo</Badge>
                    ) : (
                      <Badge variant="neutral" size="sm">Revogado</Badge>
                    )}
                  </div>
                  <p className="text-xs text-ink-3 mt-0.5">
                    Criado {formatDateTime(l.criadoEmIso)} · {l.acessos} acesso(s)
                    {l.ultimoAcessoIso && ` · Último: ${formatDateTime(l.ultimoAcessoIso)}`}
                  </p>
                </div>
                {l.ativo && (
                  <div className="flex items-center gap-1">
                    {/* M-7 (BUG-V2-030): Copy link absoluto pra WhatsApp/email. */}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(toAbsoluteUrl(`/os/${l.token}`))}
                      data-testid={`copy-link-${l.token}`}
                      title="Copiar link público"
                    >
                      <Copy className="w-4 h-4 mr-1.5" aria-hidden="true" />
                      Copiar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRevoke(l)}
                      data-testid={`revoke-link-${l.token}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
                      Revogar
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
